import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, ClipboardCheck, FileText, MapPin, Minus, Plus } from "lucide-react";
import type { CreateCatalogRequest } from "@/lib/api/catalog";
import type { ItemType } from "@/lib/api/item-types";
import type { Location } from "@/lib/api/locations";
import type { BookCondition } from "@/lib/api/book-conditions";
import type { ItemStatus } from "@/lib/api/item-statuses";
import type { LucideIcon } from "lucide-react";

type ItemDefaults = CreateCatalogRequest["itemDefaults"];
type SelectOption = { value: string; label: string };

interface AddCopyItemDefaultsGridProps {
  copies: number;
  onCopiesChange: (copies: number) => void;
  itemDefaults: ItemDefaults;
  onItemDefaultsChange: (defaults: ItemDefaults) => void;
  itemTypes: ItemType[];
  locations: Location[];
  conditions: BookCondition[];
  itemStatuses: ItemStatus[];
  hideCopies?: boolean;
}

function clampCopies(value: number) {
  return Math.max(1, Math.min(100, value));
}

function DefaultSelect({
  label,
  icon: Icon,
  value,
  placeholder,
  onChange,
  options,
}: {
  label: string;
  icon: LucideIcon;
  value: string;
  placeholder: string;
  onChange: (v: string) => void;
  options: SelectOption[];
}) {
  return (
    <div>
      <Label className="flex items-center gap-1.5">
        <Icon className="h-4 w-4 text-gray-400" />
        {label} <span className="text-red-500">*</span>
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function AddCopyItemDefaultsGrid({
  copies,
  onCopiesChange,
  itemDefaults,
  onItemDefaultsChange,
  itemTypes,
  locations,
  conditions,
  itemStatuses,
  hideCopies = false,
}: AddCopyItemDefaultsGridProps) {
  const update = (patch: Partial<ItemDefaults>) =>
    onItemDefaultsChange({ ...itemDefaults, ...patch });

  return (
    <>
      {!hideCopies ? (
        <div>
          <Label>Number of Copies</Label>
          <div className="mt-1.5 flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onCopiesChange(clampCopies(copies - 1))}
              disabled={copies <= 1}
            >
              <Minus className="h-4 w-4" />
            </Button>
            <Input
              type="number"
              min={1}
              max={100}
              value={copies}
              onChange={(e) => onCopiesChange(clampCopies(parseInt(e.target.value, 10) || 1))}
              className="w-20 text-center"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onCopiesChange(clampCopies(copies + 1))}
              disabled={copies >= 100}
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : null}
      <DefaultSelect
        label="Item Type"
        icon={FileText}
        value={itemDefaults.itemTypeId || ""}
        placeholder="Select type"
        onChange={(v) => update({ itemTypeId: v || undefined })}
        options={itemTypes.map((t) => ({ value: t.id, label: t.name }))}
      />
      <DefaultSelect
        label="Default Location"
        icon={MapPin}
        value={itemDefaults.locationId || ""}
        placeholder="Select location"
        onChange={(v) => update({ locationId: v || undefined })}
        options={locations.map((l) => ({ value: l.id, label: l.name }))}
      />
      <DefaultSelect
        label="Default Condition"
        icon={ClipboardCheck}
        value={itemDefaults.conditionCode || ""}
        placeholder="Select condition"
        onChange={(v) => update({ conditionCode: v || undefined })}
        options={conditions.map((c) => ({ value: c.code, label: c.label }))}
      />
      <DefaultSelect
        label="Default Status"
        icon={CheckCircle}
        value={itemDefaults.statusCode || "AVAILABLE"}
        placeholder="Select status"
        onChange={(v) => update({ statusCode: v || "AVAILABLE" })}
        options={itemStatuses.map((s) => ({ value: s.code, label: s.label }))}
      />
    </>
  );
}
